import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '@shared/services/api';
import { Card, Button, Input } from '@shared/components/ui';
import { useImpersonationGuard } from '@shared/components/ImpersonationGuard';
import { Lock, CheckCircle2, AlertCircle, ArrowLeft, ShieldAlert, Zap } from 'lucide-react';

export default function ChangePassword() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { shouldDisable } = useImpersonationGuard();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const requirements = [
    { text: t('changePassword.requirements.minLength'), met: newPassword.length >= 12 },
    {
      text: t('changePassword.requirements.upperLower'),
      met: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword),
    },
    {
      text: t('changePassword.requirements.numberSpecial'),
      met: /\d/.test(newPassword) && /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    },
  ];

  const allMet = requirements.every((r) => r.met);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || shouldDisable || !allMet || newPassword !== confirmPassword) return;
    setLoading(true);
    setError('');
    try {
      await authService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess(true);
    } catch {
      setError(t('changePassword.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    sessionStorage.clear();
    window.location.href = '/login';
  };

  if (shouldDisable) {
    return (
      <div className="max-w-xl mx-auto py-20 text-center animate-in zoom-in duration-500">
        <div className="w-20 h-20 bg-state-warn-bg-light dark:bg-state-warn-bg-dark rounded-full flex items-center justify-center mx-auto mb-6 text-state-warn-light dark:text-state-warn-dark">
          <ShieldAlert size={40} />
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
          {t('changePassword.impersonation.title')}
        </h2>
        <p className="mt-4 text-text-muted-light dark:text-text-muted-dark font-medium leading-relaxed">
          {t('changePassword.impersonation.description')}
        </p>
        <Button variant="outline" className="mt-8" onClick={() => navigate(-1)}>
          {t('changePassword.impersonation.goBack')}
        </Button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-md mx-auto py-20 text-center animate-in zoom-in duration-500">
        <div className="w-20 h-20 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-full flex items-center justify-center mx-auto mb-6 text-state-ok-light dark:text-state-ok-dark">
          <CheckCircle2 size={40} />
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
          {t('changePassword.success.title')}
        </h2>
        <p className="mt-4 text-text-muted-light dark:text-text-muted-dark font-medium">
          {t('changePassword.success.description')}
        </p>
        <Button className="w-full mt-8" onClick={handleLogout}>
          {t('changePassword.success.logout')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-300">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('changePassword.backToProfile')}
            disabled={loading}
            onClick={() => navigate('/profile')}
            className="p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
          >
            <ArrowLeft size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              {t('changePassword.title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
              {t('changePassword.subtitle')}
            </p>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 p-4 rounded-xl flex items-center gap-3 text-state-danger-light dark:text-state-danger-dark font-bold"
          >
            <AlertCircle size={20} /> {error}
          </div>
        )}

        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <fieldset disabled={loading} className="space-y-8">
              <div>
                <label
                  htmlFor="current-password"
                  className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
                >
                  {t('changePassword.currentPassword')}
                </label>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark"
                  />
                  <input
                    id="current-password"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="new-password"
                    className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
                  >
                    {t('changePassword.newPassword')}
                  </label>
                  <div className="relative">
                    <Zap
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark"
                    />
                    <input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                </div>

                <ul aria-live="polite" className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {requirements.map((r, i) => (
                    <li
                      key={i}
                      className={`px-3 py-2 rounded-xl border text-xs font-semibold tracking-wide transition-colors ${
                        r.met
                          ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                          : 'bg-surface-2-light dark:bg-surface-2-dark border-surface-border-light dark:border-surface-border-dark text-text-subtle-light dark:text-text-subtle-dark'
                      }`}
                    >
                      {r.met ? '✓ ' : '○ '}
                      {r.text}
                    </li>
                  ))}
                </ul>
              </div>

              <Input
                id="confirm-password"
                label={t('changePassword.confirmPassword')}
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                error={
                  confirmPassword && newPassword !== confirmPassword
                    ? t('changePassword.passwordsDontMatch')
                    : undefined
                }
              />

              <div className="pt-6 border-t border-surface-border-light dark:border-surface-border-dark">
                <Button
                  type="submit"
                  className="w-full"
                  isLoading={loading}
                  disabled={!allMet || newPassword !== confirmPassword}
                >
                  {t('changePassword.submit')}
                </Button>
              </div>
            </fieldset>
          </form>
        </Card>
      </div>
    </>
  );
}
